import { useEffect } from "react";
import { isMobileApp } from "@shared/lib/webview-detection";

const KEYBOARD_OPEN_THRESHOLD_PX = 120;

const isTextInputElement = (target: EventTarget | null): target is HTMLElement => {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) {
    return !["checkbox", "radio", "range", "file", "button", "submit", "reset"].includes(target.type);
  }
  return target.isContentEditable;
};

/**
 * Global runtime enhancements for mobile webview containers:
 * - keeps a stable viewport-height css variable
 * - marks keyboard-open state on html/body
 * - auto-scrolls focused inputs into view
 */
export function useWebViewNativeExperience(): void {
  useEffect(() => {
    if (typeof window === "undefined" || !isMobileApp()) return;

    const root = document.documentElement;
    const body = document.body;
    const viewport = window.visualViewport;
    let keyboardOpen = false;

    root.classList.add("webview-app");
    body.classList.add("webview-app");

    const syncViewport = () => {
      const viewportHeight = viewport?.height ?? window.innerHeight;
      const offsetTop = viewport?.offsetTop ?? 0;
      const keyboardInset = Math.max(0, window.innerHeight - viewportHeight - offsetTop);

      root.style.setProperty("--app-vh", `${Math.round(viewportHeight)}px`);
      root.style.setProperty("--keyboard-inset", `${Math.round(keyboardInset)}px`);

      const nextKeyboardOpen = keyboardInset > KEYBOARD_OPEN_THRESHOLD_PX;
      if (nextKeyboardOpen !== keyboardOpen) {
        keyboardOpen = nextKeyboardOpen;
        root.classList.toggle("keyboard-open", keyboardOpen);
        body.classList.toggle("keyboard-open", keyboardOpen);
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!isTextInputElement(event.target)) return;
      const target = event.target;
      window.setTimeout(() => {
        if (!target.isConnected) return;
        target.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      }, 140);
      syncViewport();
    };

    const handleFocusOut = () => {
      window.setTimeout(syncViewport, 120);
    };

    syncViewport();

    window.addEventListener("resize", syncViewport);
    viewport?.addEventListener("resize", syncViewport);
    viewport?.addEventListener("scroll", syncViewport);
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);

    return () => {
      window.removeEventListener("resize", syncViewport);
      viewport?.removeEventListener("resize", syncViewport);
      viewport?.removeEventListener("scroll", syncViewport);
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);

      root.classList.remove("webview-app", "keyboard-open");
      body.classList.remove("webview-app", "keyboard-open");
      root.style.removeProperty("--app-vh");
      root.style.removeProperty("--keyboard-inset");
    };
  }, []);
}

